import { Input as ChakraInput, InputProps as ChakraInputProps } from '@chakra-ui/react';
import { FieldValues, UseControllerProps } from 'react-hook-form';

import { useField } from '#features/form/hooks/useField';
import { BaseInput } from '../BaseInput';
import { toDomProps } from '../splitFieldProps';
import { InputProps } from '../types';

const INTEGER_FILTER_EVENT_KEY = new Set(['.', 'e']);
const types = {
  TEXT: 'text',
  NUMBER: 'number',
} as const;

type NumberProps = {
  type: 'number';
  min?: number;
  max?: number;
};

type TextProps = { type?: 'text' | 'email' };

type TextInputProps<T> = InputProps<T> &
  (NumberProps | TextProps) &
  Omit<ChakraInputProps, keyof InputProps<T> | keyof NumberProps | keyof TextProps> & {
    /** Ссылка формы на само поле — по ней в него ставят курсор. Идёт в `<input>`, не в обёртку. */
    ref?: React.Ref<HTMLInputElement>;
  };

export const TextInput = (props: TextInputProps<string>) => {
  /*
    `ref` достаётся полю, а не обёртке.

    Через него форма находит поле, когда нужно поставить в него курсор - при отказе сервера
    («этот ник занят») и при своей проверке. Раньше он уезжал и в `BaseInput`, а тот вешает его
    на `Field.Root`, то есть на `div`: React зовёт ссылки снизу вверх, обёртка оказывалась
    последней и перебивала поле. Форма честно просила курсор, звала `focus()` у `div` - и ничего
    не происходило.
  */
  const { type, onChange, value = '', ref, ...inputProps } = props;
  const domProps = toDomProps(inputProps);

  const handleChange = ({ target: { value: _value } }: React.ChangeEvent<HTMLInputElement>) => {
    if (type === types.NUMBER) {
      // Значение из DOM — всегда строка, поэтому и в форму уходит строка (тип поля — string);
      // приведение к числу остаётся схеме валидации. Пропускаем только пустое значение и целые
      // цифры. Верхнюю границу проверяем на каждом вводе (больше максимума набрать нельзя), а
      // нижнюю нет: пока человек набирает «25» при минимуме 18, «2» тоже меньше минимума.
      const { max } = props;
      const withinMax = typeof max !== 'number' || Number(_value) <= max;
      if (!_value || (/^\d+$/.test(_value) && withinMax)) {
        onChange?.(_value);
      }
    } else {
      onChange?.(_value);
    }
  };

  const onKeyDown = (ev: React.KeyboardEvent<HTMLInputElement>) => {
    if (type === types.NUMBER && INTEGER_FILTER_EVENT_KEY.has(ev.key)) {
      ev.preventDefault();
      ev.stopPropagation();
    }
  };

  const inputType = type === types.NUMBER ? 'text' : type;

  return (
    <BaseInput {...inputProps}>
      <ChakraInput
        ref={ref}
        {...{ type: inputType, onChange: handleChange, onKeyDown, value }}
        {...domProps}
      />
    </BaseInput>
  );
};

export type TextInputFieldProps<T extends FieldValues> = UseControllerProps<T> &
  Omit<TextInputProps<T>, 'value' | 'defaultValue' | 'ref'>;

export const TextInputField = <T extends FieldValues>({
  name,
  control,
  defaultValue = '',
  onChange,
  ...props
}: TextInputFieldProps<T>) => {
  const fieldProps = useField({ control, defaultValue, name, onChange });

  return <TextInput {...fieldProps} {...props} />;
};
